import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Stack, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';

import {
  formatKwh, formatPercent, formatTonne,
} from '../../../shared/utils/number';
import { MONO_FONT, SCOPE_TOKENS } from '../../../theme/muiTheme';

const COLORS = [
  '#14b8a6', '#38bdf8', '#8b5cf6', '#f472b6',
  '#22c55e', '#fb923c', '#06b6d4', '#6366f1',
];

function OfficeCell(props) {
  const {
    depth, x, y, width, height, name, percent, color,
  } = props;
  if (depth !== 1 || width <= 0 || height <= 0) return null;
  const showName = width > 125 && height > 65;
  const showPercent = width > 58 && height > 34;
  const shortName = name?.length > 22 ? `${name.slice(0, 20)}…` : name;

  return (
    <g>
      <rect
        x={x + 2}
        y={y + 2}
        width={Math.max(width - 4, 0)}
        height={Math.max(height - 4, 0)}
        rx={6}
        ry={6}
        fill={color}
        stroke="#fff"
        strokeWidth={2}
      />
      {showName && (
        <text
          x={x + width / 2}
          y={y + height / 2 - 5}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={12}
          fontWeight={700}
        >
          {shortName}
        </text>
      )}
      {showPercent && (
        <text
          x={x + width / 2}
          y={y + height / 2 + (showName ? 14 : 1)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontFamily={MONO_FONT}
          fontSize={showName ? 14 : 10}
          fontWeight={800}
        >
          {formatPercent(percent)}
        </text>
      )}
    </g>
  );
}

function OfficeRankRow({ office, index }) {
  const theme = useTheme();
  const highlighted = index < 3;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        bgcolor: highlighted ? alpha(office.color, theme.palette.mode === 'dark' ? 0.14 : 0.07) : 'transparent',
        border: 1,
        borderColor: highlighted ? alpha(office.color, 0.65) : 'divider',
        borderRadius: 1.5,
        minHeight: 36,
        px: 1,
        py: 0.65,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <Box sx={{ alignItems: 'center', bgcolor: highlighted ? office.color : 'action.selected', borderRadius: '50%', color: highlighted ? '#fff' : 'text.secondary', display: 'flex', flex: '0 0 auto', fontFamily: MONO_FONT, fontSize: 10, fontWeight: 800, height: 20, justifyContent: 'center', width: 20 }}>
        {index + 1}
      </Box>
      <Box sx={{ bgcolor: office.color, borderRadius: '2px', flex: '0 0 auto', height: 8, width: 8 }} />
      <Typography variant="caption" sx={{ flex: 1, fontWeight: highlighted ? 700 : 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {office.name}
      </Typography>
      <Typography variant="caption" sx={{ color: SCOPE_TOKENS.SCOPE_2.dark, flexShrink: 0, fontFamily: MONO_FONT, fontWeight: 800 }}>
        {formatTonne(office.emissions, { withUnit: true })}
      </Typography>
      <Typography variant="caption" sx={{ color: office.color, flexShrink: 0, fontFamily: MONO_FONT, fontWeight: 800, textAlign: 'right', width: 48 }}>
        {formatPercent(office.percent)}
      </Typography>
    </Stack>
  );
}

function OfficeTooltip({ active, payload, showActivity = true }) {
  const { t } = useTranslation();
  if (!active || !payload?.[0]?.payload) return null;
  const item = payload[0].payload;

  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1.5, boxShadow: 3, maxWidth: 300, p: 1.25 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>{item.name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {item.legalEntityCode || item.legalEntity || t('scopeEmissions.scope2.unknownLegalEntity')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO_FONT, fontWeight: 750 }}>
        {formatTonne(item.emissions, { withUnit: true })} · {formatPercent(item.percent)}
      </Typography>
      {showActivity && (
        <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO_FONT }}>
          {formatKwh(item.kwh, { withUnit: true })}
        </Typography>
      )}
    </Box>
  );
}

export default function OfficeEmissionsTreemap({
  offices, showActivity = true,
}) {
  const { t } = useTranslation();
  const total = offices.reduce((sum, office) => sum + office.emissions, 0);
  const data = [...offices]
    .sort((left, right) => right.emissions - left.emissions || left.name.localeCompare(right.name))
    .map((office, index) => ({
      ...office,
      color: COLORS[index % COLORS.length],
      percent: total > 0 ? (office.emissions / total) * 100 : 0,
      rank: index + 1,
    }));

  if (total <= 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ color: 'text.secondary', minHeight: 220, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('scopeEmissions.scope2.treemapEmpty')}</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box
        role="group"
        aria-label={t('scopeEmissions.scope2.treemapAria')}
        sx={{
          height: { xs: 280, sm: 300, md: 300 },
          minWidth: 0,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data}
            dataKey="emissions"
            content={<OfficeCell />}
            isAnimationActive={false}
          >
            <Tooltip content={<OfficeTooltip showActivity={showActivity} />} />
          </Treemap>
        </ResponsiveContainer>
      </Box>
      <Stack spacing={0.75} sx={{ maxHeight: 150, overflowY: 'auto', pr: 0.5 }}>
        {data.map((office, index) => (
          <OfficeRankRow
            key={office.id}
            office={office}
            index={index}
          />
        ))}
      </Stack>
    </Stack>
  );
}